import { apiService } from './apiService';

export interface DemurrageSettings {
  enabled: boolean;
  transitionDate: string;
  dailyFee: number;
  geofenceKm: number;
  customerCutoffTime: string;
  depotCutoffTime: string;
}

export interface DemurrageCalcResult {
  shipmentId: number;
  shipmentNo: string;
  plate: string;
  driverName: string;
  deliveryName: string;
  deliveryType: 'customer' | 'depot';
  city: string;
  district: string;
  arrivalAt: string | null;
  unloadingAt: string | null;
  withinGeofence: boolean;
  arrivedByCutoff: boolean;
  firstDayCondition: boolean;
  nDays: number;
  totalAmount: number;
  notes: string;
}

export interface DemurrageReportRow {
  shipmentId: string;
  shipmentNo: string;
  orderCode: string;
  planCode: string;
  transportModel: string;
  plate: string;
  driverName: string;
  facilityName: string;
  deliveryName: string;
  deliveryType: 'customer' | 'depot';
  city: string;
  district: string;
  pickupDate: string | null;
  assignedAt: string | null;
  firstArrivalAt: string | null;
  lastDepartureAt: string | null;
  hasLogWindow?: boolean;
  arrivalAt: string | null;
  unloadingAt: string | null;
  autoApplicable: boolean;
  withinGeofence: boolean;
  arrivedByCutoff: boolean;
  firstDayCondition: boolean;
  eligible: boolean;
  reflected?: boolean;
  reflectedAmount?: number;
  nDays: number;
  totalAmount: number;
  status: string;
  notes: string;
}

export interface DemurrageReport {
  settings: {
    transitionDate: string;
    dailyFee: number;
    geofenceKm: number;
    customerCutoffTime: string;
    depotCutoffTime: string;
    active: boolean;
  };
  summary: {
    shipmentCount: number;
    eligibleCount: number;
    ineligibleCount: number;
    totalDays: number;
    totalAmount: number;
  };
  details: DemurrageReportRow[];
  byFacility: Array<{ key: string; count: number; totalDays: number; totalAmount: number }>;
  byCity: Array<{ key: string; count: number; totalDays: number; totalAmount: number }>;
  byDistrict: Array<{ key: string; count: number; totalDays: number; totalAmount: number }>;
}

export const demurrageService = {
  async getSettings() {
    return await apiService.get<DemurrageSettings>('/demurrage/settings');
  },

  async calculate(shipmentIds: Array<string | number>) {
    return await apiService.post<DemurrageCalcResult[]>('/demurrage/calculate', { shipmentIds });
  },

  async getReport(params?: { from?: string; to?: string; warehouseId?: string | number; plate?: string; eventFrom?: string; eventTo?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.warehouseId) queryParams.append('warehouseId', String(params.warehouseId));
    if (params?.plate) queryParams.append('plate', params.plate);
    if (params?.eventFrom) queryParams.append('eventFrom', params.eventFrom);
    if (params?.eventTo) queryParams.append('eventTo', params.eventTo);
    const query = queryParams.toString();
    return await apiService.get<DemurrageReport>(`/demurrage/report${query ? `?${query}` : ''}`, { timeoutMs: 300000 });
  },

  async applyToShipments(shipmentIds: Array<string | number>) {
    return await apiService.post('/demurrage/apply-to-shipments', { shipmentIds }, { timeoutMs: 300000 });
  },

  async correctToShipments(shipmentIds: Array<string | number>) {
    return await apiService.post('/demurrage/correct-to-shipments', { shipmentIds }, { timeoutMs: 300000 });
  },
};
